import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { coupleNames } from '@/content/site'
import { LanguageToggle } from '@/components/LanguageToggle'
import { useAuth } from '@/context/AuthContext'

export function SiteHeader() {
  const { t } = useTranslation()
  const { user, logout } = useAuth()
  const [isAdmin, setIsAdmin] = React.useState(false)
  const [isMenuOpen, setIsMenuOpen] = React.useState(false)
  const [isDarkSection, setIsDarkSection] = React.useState(false)

  React.useEffect(() => {
    if (user) {
      user.getIdTokenResult().then((idTokenResult: any) => {
        setIsAdmin(idTokenResult.claims.role === 'admin');
      });
    } else {
      setIsAdmin(false);
    }
  }, [user]);

  React.useEffect(() => {
    const handleScroll = () => {
      const header = document.querySelector('header');
      if (!header) return;

      const headerRect = header.getBoundingClientRect();
      const checkY = headerRect.top + headerRect.height / 2;

      const darkSections = document.querySelectorAll('[data-theme="dark"]');
      let isDark = false;
      darkSections.forEach(section => {
        const rect = section.getBoundingClientRect();
        if (checkY >= rect.top && checkY <= rect.bottom) {
          isDark = true;
        }
      });
      setIsDarkSection(isDark);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    // Run initially and after a slight delay to ensure elements are rendered
    handleScroll();
    setTimeout(handleScroll, 100);

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { href: '/#schedule', label: t('nav.itinerary') },
    { href: '/#travel', label: t('nav.travel') },
    // { href: '/#contact', label: t('nav.contact') },
    { href: '/#faq', label: t('nav.faq') },
    { href: '/rsvp', label: user ? t('nav.my_rsvp') : t('nav.rsvp') },
    ...(isAdmin ? [{ href: '/admin', label: t('nav.admin') }] : []),
  ]

  const handleLogout = async () => {
    try {
      await logout();
      window.location.href = '/'; // Simple redirect after logout
    } catch (e) {
      console.error("Logout failed", e);
    }
  }

  return (
    <header className={`sticky top-0 z-50 w-full backdrop-blur-md border-b transition-all duration-300 ${isDarkSection ? 'bg-black/10 border-white/10' : 'bg-nav-bg/10 border-black/5'}`}>
      <div className="shell flex items-center justify-between h-10">
        <div className="flex items-center justify-between w-full md:w-auto">
          <Link className={`text-xl font-display uppercase tracking-[0.25em] hover:opacity-80 transition-opacity duration-300 ${isDarkSection ? 'text-[#efebe4]' : 'text-brand-accent'}`} to="/" translate="no">
            {coupleNames}
          </Link>

          {/* Mobile Menu Button - visible on small screens */}
          <button
            className={`md:hidden p-2 transition-colors focus:outline-none ${isDarkSection ? 'text-[#efebe4] hover:text-[#A89461]' : 'text-secondary hover:text-primary'}`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            <span className="block w-6 h-0.5 bg-current mb-1.5 transition-transform duration-300"></span>
            <span className="block w-6 h-0.5 bg-current mb-1.5 transition-opacity duration-300"></span>
            <span className="block w-6 h-0.5 bg-current transition-transform duration-300"></span>
          </button>
        </div>

        <nav className={`${isMenuOpen ? 'flex flex-col absolute top-full left-0 w-full bg-white p-6 shadow-xl border-b border-gray-100 space-y-4 animate-fade-in' : 'hidden md:flex items-center gap-4 ml-auto mr-16'}`} aria-label="Primary">
          {navItems.map((item, index) => (
            <React.Fragment key={item.href}>
              <Link
                className={`text-base font-sans font-medium tracking-[1.5px] transition-colors duration-200 ${['RSVP', 'FAQ'].includes(item.label) ? 'uppercase' : 'lowercase'} ${isMenuOpen ? 'text-nav-text/90 hover:text-[#A89461]' : (isDarkSection ? 'text-[#efebe4]/90 hover:text-white' : 'text-nav-text/90 hover:text-[#A89461]')}`}
                to={item.href}
                onClick={(e) => {
                  setIsMenuOpen(false);
                  if (item.href.startsWith('/#')) {
                    const id = item.href.split('#')[1];
                    const element = document.getElementById(id);
                    if (element) {
                      e.preventDefault();
                      element.scrollIntoView({ behavior: 'smooth' });
                      // Update URL without jump
                      window.history.pushState(null, '', item.href);
                    }
                  }
                }}
              >
                {item.label}
              </Link>
              {index < navItems.length - 1 && (
                <span className={`hidden md:inline font-light px-1 ${isDarkSection ? 'text-[#efebe4]/30' : 'text-nav-text/30'}`}>|</span>
              )}
            </React.Fragment>
          ))}
          {/* Mobile Extras (Language Toggle & Logout) */}
          <div className="md:hidden pt-4 border-t border-gray-100 space-y-6">
            <div className="flex flex-col gap-2">
              <span className="text-[10px] font-sans font-bold uppercase tracking-[0.2em] text-nav-text/40">{t('nav.language') || 'Language'}</span>
              <LanguageToggle />
            </div>
            {user && (
              <button
                className="text-left text-base font-sans font-medium uppercase tracking-[1.5px] text-nav-text/90 hover:text-[#A89461] transition-colors duration-200 w-full"
                onClick={() => {
                  handleLogout();
                  setIsMenuOpen(false);
                }}
              >
                {t('nav.logout')}
              </button>
            )}
          </div>
        </nav>

        <div className={`hidden md:flex items-center gap-6 ${isDarkSection ? 'text-[#efebe4]' : ''}`}>
          {user && (
            <button
              onClick={handleLogout}
              className={`text-base font-sans font-medium tracking-[1.5px] transition-colors duration-200 uppercase ${isDarkSection ? 'text-[#efebe4]/90 hover:text-white' : 'text-nav-text/90 hover:text-[#A89461]'}`}
            >
              {t('nav.logout')}
            </button>
          )}
          <LanguageToggle />
        </div>
      </div>
    </header>
  )
}
